import { useCallback, useEffect, useState } from 'react'
import {
  Autocomplete,
  Checkbox,
  TextField,
  Typography,
  IconButton,
  Collapse,
  Box,
  List,
  ListItem,
  ListItemText
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

const FeatureGroupSelect = ({ groups = [], acceptFromDialog = false, partReferenceId, resourceType }) => {
  const [selectedGroups, setSelectedGroups] = useState([])
  const [expandedGroupIds, setExpandedGroupIds] = useState([])
  const { t_ } = useSmartTranslation()
  const ALL_OPTION = { id: '__all__', name: t_('Select All') }
  const options = [ALL_OPTION, ...groups]

  const toggleExpanded = (id) => {
    setExpandedGroupIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }
  const handleSaveFeatureGroups = useCallback(async () => {
    const allGroupsFeatures = selectedGroups.flatMap(group => group.features)
    await apiPost('featureResourceValue/' + resourceType + '/' + partReferenceId, allGroupsFeatures)
  }, [selectedGroups, partReferenceId, resourceType])

  useEffect(() => {
    if (acceptFromDialog && selectedGroups && selectedGroups.length > 0) {
      handleSaveFeatureGroups()
    }
  }, [acceptFromDialog, handleSaveFeatureGroups, selectedGroups])

  const handleToggleSelect = (group) => {
    if (group.id === '__all__') {
      const allSelected = selectedGroups.length === groups.length
      setSelectedGroups(allSelected ? [] : groups)
    } else {
      setSelectedGroups((prevSelected) => {
        const exists = prevSelected.some((g) => g.id === group.id)
        return exists
          ? prevSelected.filter((g) => g.id !== group.id)
          : [...prevSelected, group]
      })
    }
  }

  const isChecked = (group) => {
    if (group.id === '__all__') {
      return groups.length > 0 && selectedGroups.length === groups.length
    }
    return selectedGroups.some((g) => g.id === group.id)
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      clearIcon={null}
      options={options}
      value={selectedGroups}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(event, value) => {
        if (value.some(v => v.id === '**all**')) {
          const allSelected = selectedGroups.length === groups.length
          setSelectedGroups(allSelected ? [] : groups)
        } else {
          setSelectedGroups(value)
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t_('Groups')}
          InputLabelProps={{ shrink: true }}
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.id}
          onClick={() => handleToggleSelect(option)}
        >
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={isChecked(option)}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleSelect(option)
                  }}
                  sx={{ mr: 1 }}
                />
                <Typography
                  variant='body1'
                  sx={{ cursor: 'pointer' }}
                >
                  {option.name}
                </Typography>
              </Box>
              {option.id !== '__all__' && (
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpanded(option.id)
                  }}
                >
                  {expandedGroupIds.includes(option.id)
                    ? <ExpandLessIcon />
                    : <ExpandMoreIcon />}
                </IconButton>
              )}
            </Box>

            {option.id !== '__all__' && (
              <Collapse
                in={expandedGroupIds.includes(option.id)}
                timeout='auto'
                unmountOnExit
              >
                <List dense disablePadding sx={{ ml: 4, mt: 1 }}>
                  {(option.features || []).map((feature) => (
                    <ListItem key={feature.id} sx={{ pl: 2 }} disableGutters>
                      <ListItemText
                        primary={feature.name}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        </li>
      )}
    />
  )
}

export default FeatureGroupSelect
